import React from 'react';
import { Parallax, Breadcrumbs, Button } from 'components';
import styled from 'styled-components';
import { useWindowSize } from 'hooks';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';

export const Grid = styled.section`
  background-color: #fff;
  display: grid;
  gap: 0.625rem;
  margin: 0 auto;
  max-width: 88.75rem;
  padding: 40px 0.625rem 0;
  place-items: start center;
  width: 100%;

  @media (width >= 450px) {
    grid-template-columns: repeat(2, minmax(10rem, 1fr));
  }

  @media (width >= 768px) {
    grid-template-columns: repeat(9, 1fr);
    padding: 6.813rem 0.625rem 0;
  }

  @media (width >= 1200px) {
    grid-template-columns: repeat(12, 1fr);
  }

  @media (width >= 1440px) {
    grid-template-columns: repeat(12, minmax(0, 100px));
    max-width: initial;
    gap: 1.25rem;
    justify-content: center;
  }
`;

export function Hero({
  heroImageOffset = -145,
  gradientPercentage = '87.5%',
  filter = 'contrast(120%) saturate(155%)',
  heroImage,
  heroImageAlt,
  heroImageTitle,
  removeProductNameFromPath,
  children,
  breadcrumb,
  showRequestAQuoteButton
}) {
  const { windowWidth } = useWindowSize();
  return (
    <HeroContainer>
      <StyledParallax>
        <HeroImage
          image={getImage(heroImage)}
          filter={filter}
          $heroImageOffset={heroImageOffset}
          alt={heroImageAlt}
          title={heroImageTitle}
          loading="eager"
        />
      </StyledParallax>
      <BlackGradient $gradientPercentage={gradientPercentage} />
      <BrownGradient $gradientPercentage={gradientPercentage} />
      <Text>
        <StyledBreadcrumbs
          breadcrumb={breadcrumb}
          removeProductNameFromPath={removeProductNameFromPath}
        />
        {children}
        {showRequestAQuoteButton && windowWidth >= 768 && (
          <StyledButton
            to="/request-a-quote/"
            shine
            shineDuration={3}
            style={{ width: '164px', whiteSpace: 'nowrap' }}>
            Request A Quote
          </StyledButton>
        )}
      </Text>
      {showRequestAQuoteButton && windowWidth < 768 && (
        <StyledButton
          to="/request-a-quote/"
          shine
          shineDuration={3}
          style={{ width: '164px', whiteSpace: 'nowrap' }}>
          Request A Quote
        </StyledButton>
      )}
    </HeroContainer>
  );
}

const StyledButton = styled(Button)`
  grid-column: 1/-1;
  grid-row: -1;
  margin-top: 22px !important;

  @media (width >= 768px) {
    grid-column: 10/12;
    grid-row: 2;
    place-self: start end;
    margin-top: 26px !important;
  }
`;

const StyledParallax = styled(Parallax)`
  grid-column: 1/-1;
  grid-row: 1/-1;
  left: -10px;
  position: relative;
`;

const BlackGradient = styled.div`
  background: ${({ $gradientPercentage }) =>
    `linear-gradient(330deg, transparent 3.44%, rgba(000 000 000 / 93.3%) ${$gradientPercentage})`};
  background-blend-mode: plus-darker;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`;

const BrownGradient = styled.div`
  background: ${({ $gradientPercentage }) =>
    `linear-gradient(330deg, transparent 3.44%, rgba(73 10 12 / 10%) ${$gradientPercentage})`};
  background-blend-mode: plus-darker;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`;

const HeroImage = styled(GatsbyImage)`
  filter: ${({ filter }) => filter};
  height: 720px;
  left: -6px;
  position: absolute;
  top: ${({ $heroImageOffset }) => `${$heroImageOffset}px`};
  width: 100vw;
  z-index: 0;

  @media (width >= 768px) {
    left: 0;
  }
`;

const HeroContainer = styled.section`
  align-content: start;
  column-gap: 1.25rem;
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-template-rows: auto auto auto;
  height: 530px;
  overflow: hidden;
  padding: 0 1rem;
  position: relative;

  @media (width >= 640px) {
    height: 26.25rem;
  }

  @media (width >= 768px) {
    padding: 0 0.625rem;
  }
`;

const StyledBreadcrumbs = styled(Breadcrumbs)`
  color: #fff;
  grid-column: 2/8;
  grid-row: 1;
  margin-top: 33px;
  z-index: 1;

  @media (width <= 767px) {
    display: none;
  }
`;

const Text = styled.div`
  column-gap: 1.25rem;
  display: grid;
  grid-column: 1/-1;
  grid-row: 1/-1;
  grid-template-columns: repeat(12, minmax(0, 100px));
  justify-content: center;

  h1,
  p {
    color: #fff;
    grid-column: 1/-1;
    position: relative;
    z-index: 1;

    @media (width >= 640px) {
      grid-column: 1/12;
    }

    @media (width >= 768px) {
      grid-column: 2/12;
    }
  }

  h1 {
    font-weight: 400;
    line-height: 140%;
    grid-row: 2;
    margin-top: 56px;
    max-width: 840px;

    @media (width >= 768px) {
      margin-top: 22px;
    }

    @media (width >= 1100px) {
      font-size: 3rem !important;
    }
  }

  p {
    grid-row: 3;
    line-height: 140%;
    margin-bottom: 1rem;
    margin-top: 1.4375rem;
    max-width: 40.938rem;

    @media (width >= 768px) {
      margin-bottom: 4.125rem;
    }
  }
`;
